import React from 'react';
import { AlertTriangle, FileText, Wrench } from 'lucide-react';
import { Modal } from '../../common/Modal';
import DiagnosisFallbackNotice, { isFallbackDiagnosis } from '../parts/DiagnosisFallbackNotice';

// Extracted verbatim from JobManagement's JSX (Task 8).
// The orchestrator keeps the `show*` guard, so mount/unmount semantics are
// unchanged; this component is the modal's body only.
//
// isOpen is hardcoded true: the orchestrator's `showDiagnosticDetail &&
// selectedDiagnostic &&` guard at the call site is what controls mounting.
// Because isOpen is never false, Modal's `if (!isOpen) return null` can
// never fire here — so dropping the call-site guard would not hide
// anything, it would leave a permanently mounted, permanently VISIBLE
// modal. The guard must stay.
const DiagnosticDetailModal = ({
  selectedDiagnostic,
  setSelectedDiagnostic,
  setShowDiagnosticDetail,
  // The shop's own rate; it was a hardcoded $100/hr.
  laborRate = 100
}) => (
        <Modal
          isOpen
          onClose={() => {
            setShowDiagnosticDetail(false);
            setSelectedDiagnostic(null);
          }}
          title={`Diagnostic Details - ${selectedDiagnostic.job_number}`}
          size="xl"
        >
          <div className="space-y-6">
            {/* Job Info */}
            <div className="bg-surface-page rounded-lg p-4">
              <h3 className="font-semibold text-ink mb-2">Job Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-ink-muted">Customer:</span>
                  <p className="font-medium">{selectedDiagnostic.customer_name}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Vehicle:</span>
                  <p className="font-medium">{selectedDiagnostic.vehicle}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Status:</span>
                  <p className="font-medium">{selectedDiagnostic.job_status}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Date:</span>
                  <p className="font-medium">{selectedDiagnostic.created_at ? new Date(selectedDiagnostic.created_at).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
              {selectedDiagnostic.symptom_description && (
                <div className="mt-3">
                  <span className="text-ink-muted text-sm">Symptoms:</span>
                  <p className="text-sm mt-1">{selectedDiagnostic.symptom_description}</p>
                </div>
              )}
            </div>

            {isFallbackDiagnosis(selectedDiagnostic) && <DiagnosisFallbackNotice />}

            {/* Possible Causes */}
            <div>
              <h3 className="font-semibold text-ink mb-3 flex items-center">
                <AlertTriangle className="h-5 w-5 text-warning mr-2" />
                Possible Causes
                {!isFallbackDiagnosis(selectedDiagnostic) && (
                  <span className="ml-2 text-sm font-normal text-ink-muted">
                    (Confidence: {Math.round((selectedDiagnostic.confidence_score || 0) * 100)}%)
                  </span>
                )}
              </h3>
              {selectedDiagnostic.possible_causes && selectedDiagnostic.possible_causes.length > 0 ? (
                <div className="space-y-2">
                  {selectedDiagnostic.possible_causes.map((cause, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-surface-card">
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-info-tint text-info rounded-full flex items-center justify-center text-sm font-medium mr-3">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="text-ink">
                            {typeof cause === 'string' ? cause : (cause.description || cause.cause || JSON.stringify(cause))}
                          </p>
                          {typeof cause === 'object' && cause.probability && (
                            <p className="text-sm text-ink-muted mt-1">Probability: {Math.round(cause.probability * 100)}%</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-ink-muted text-sm">No causes identified</p>
              )}
            </div>

            {/* Diagnostic Steps */}
            {selectedDiagnostic.diagnostic_steps && selectedDiagnostic.diagnostic_steps.length > 0 && (
              <div>
                <h3 className="font-semibold text-ink mb-3 flex items-center">
                  <Wrench className="h-5 w-5 text-accent mr-2" />
                  Diagnostic Steps
                </h3>
                <div className="space-y-2">
                  {selectedDiagnostic.diagnostic_steps.map((step, idx) => (
                    <div key={idx} className="flex items-start border-l-2 border-accent/30 pl-4 py-2">
                      <span className="flex-shrink-0 w-6 h-6 bg-accent/20 text-accent rounded-full flex items-center justify-center text-sm font-medium mr-3">
                        {idx + 1}
                      </span>
                      <p className="text-ink">
                        {typeof step === 'string' ? step : (step.description || step.step || JSON.stringify(step))}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Parts */}
            {selectedDiagnostic.suggested_parts && selectedDiagnostic.suggested_parts.length > 0 && (
              <div>
                <h3 className="font-semibold text-ink mb-3 flex items-center">
                  <FileText className="h-5 w-5 text-success mr-2" />
                  Suggested Parts
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border-default border rounded-lg">
                    <thead className="bg-surface-page">
                      <tr>
                        <th className="px-4 py-2 text-left text-small font-medium text-ink-muted">Part</th>
                        <th className="px-4 py-2 text-left text-small font-medium text-ink-muted">Part #</th>
                        <th className="px-4 py-2 text-left text-small font-medium text-ink-muted">Qty</th>
                        <th className="px-4 py-2 text-left text-small font-medium text-ink-muted">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-default">
                      {selectedDiagnostic.suggested_parts.map((part, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm">{part.description || part.part_name || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-ink-muted">{part.part_num || part.part_number || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm">{part.quantity || 1}</td>
                          <td className="px-4 py-2 text-sm">${parseFloat(part.estimated_cost || part.unit_cost || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-surface-page">
                      <tr>
                        <td colSpan="3" className="px-4 py-2 text-sm font-medium text-right">Total Parts:</td>
                        <td className="px-4 py-2 text-sm font-medium">${selectedDiagnostic.estimated_parts_cost.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-info-tint rounded-lg p-4">
              <h3 className="font-semibold text-ink mb-3">Repair Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {!isFallbackDiagnosis(selectedDiagnostic) && (
                  <div className="text-center">
                    <p className="text-page-title text-info">
                      {Math.round((selectedDiagnostic.confidence_score || 0) * 100)}%
                    </p>
                    <p className="text-sm text-ink-muted">Confidence</p>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-page-title text-accent capitalize">
                    {selectedDiagnostic.repair_complexity || 'N/A'}
                  </p>
                  <p className="text-sm text-ink-muted">Complexity</p>
                </div>
                <div className="text-center">
                  <p className="text-page-title text-success">
                    {selectedDiagnostic.estimated_labor_hours}h
                  </p>
                  <p className="text-sm text-ink-muted">Est. Labor</p>
                </div>
                <div className="text-center">
                  <p className="text-page-title text-ink">
                    ${(selectedDiagnostic.estimated_parts_cost + (selectedDiagnostic.estimated_labor_hours * laborRate)).toFixed(2)}
                  </p>
                  <p className="text-sm text-ink-muted">Est. Total</p>
                </div>
              </div>
            </div>

            {/* Mechanic Findings */}
            {selectedDiagnostic.mechanic_findings && (
              <div>
                <h3 className="font-semibold text-ink mb-2">Mechanic Findings</h3>
                <p className="text-ink bg-surface-page p-3 rounded-lg">{selectedDiagnostic.mechanic_findings}</p>
              </div>
            )}

            {/* AI Model Version */}
            <div className="text-small text-ink-muted text-right">
              AI Model Version: {selectedDiagnostic.ai_model_version || '1.0'}
            </div>
          </div>

          <div className="-mx-6 -mb-4 mt-2 px-6 py-4 border-t border-border-default bg-surface-page">
            <button
              onClick={() => {
                setShowDiagnosticDetail(false);
                setSelectedDiagnostic(null);
              }}
              className="w-full px-4 py-2 bg-surface-sunken text-ink rounded-lg hover:bg-surface-sunken/80"
            >
              Close
            </button>
          </div>
        </Modal>
);

export default DiagnosticDetailModal;
